import React, { useState } from 'react';
import { PropertyComment } from '../../../types/propertyComment';
import { AppUser } from '../../../types';
import { PencilIcon, TrashIcon, SpinnerIcon } from '../../Icons';

interface Props {
  comment: PropertyComment;
  currentUser: AppUser | null;
  onStartEdit: (comment: PropertyComment) => void;
  onDelete: (commentId: string) => Promise<boolean>;
}

export const PropertyCommentItem: React.FC<Props> = ({
  comment,
  currentUser,
  onStartEdit,
  onDelete,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isAuthor = Boolean(currentUser?.id && comment.userId === currentUser.id);
  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPERADMIN';
  const canModify = isAuthor || isAdmin;

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(comment.id);
    setIsDeleting(false);
    setConfirmDelete(false);
  };

  const formattedDate = new Date(comment.createdAt).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="bg-white border border-teal-100 rounded-2xl p-4 sm:p-5 shadow-sm transition-all hover:border-teal-200">
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-black text-xs uppercase shadow-sm">
            {(comment.userName || 'C').charAt(0)}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-900">{comment.userName}</span>
              <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 border border-teal-100">
                Comercial
              </span>
            </div>
            <span className="text-[11px] text-gray-400">
              {formattedDate} {comment.updatedAt && comment.updatedAt !== comment.createdAt && '• (editado)'}
            </span>
          </div>
        </div>

        {canModify && (
          <div className="flex items-center gap-1">
            {!confirmDelete ? (
              <>
                <button
                  type="button"
                  onClick={() => onStartEdit(comment)}
                  title="Editar comentario"
                  className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  title="Eliminar comentario"
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-1.5 bg-red-50 px-2 py-1 rounded-lg border border-red-100">
                <span className="text-[10px] text-red-700 font-bold">¿Borrar?</span>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={handleDelete}
                  className="px-2 py-0.5 text-[10px] font-black uppercase bg-red-600 text-white rounded hover:bg-red-700"
                >
                  {isDeleting ? <SpinnerIcon className="w-3 h-3 animate-spin" /> : 'Sí'}
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setConfirmDelete(false)}
                  className="px-1.5 py-0.5 text-[10px] font-medium text-gray-500 hover:text-gray-700"
                >
                  No
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap leading-relaxed pl-10">
        {comment.content}
      </p>
    </div>
  );
};
